import PhoenixOffcanvas from "../../base/PhoenixOffcanvas";
import Section from "../../base/Section";
import { useState, useEffect } from "react";
import { Button, Col, Pagination, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ProductFilterItems from "./ProvidersFilterItems";
import Scrollbar from "../../base/Scrollbar";
import ProductCard from "../../common/ProductCard";
import { useSearchParams } from "react-router-dom";
import {
  faChevronLeft,
  faChevronRight,
  faSliders,
} from "@fortawesome/free-solid-svg-icons";
import { DropdownSortProviders } from "../../common/DropdownSortProviders";
import {
  getImageByPosition,
  getLocations,
  getLocationsByName,
  getOrderObject,
  getProviderCategories,
  getProviderCategoriesByName,
  getRating,
  getSubCategories,
  isVerifiedUser,
} from "../../../helpers/utils";
import { getProviders } from "../../../api";

interface Filters {
  location: string[];
  category: string[];
  // subCategory: string[];
  rating: string | null;
  verified: string | null;
}

const ProvidersFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [show, setShow] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    location: [],
    category: [],
    // subCategory: [],
    rating: null,
    verified: null,
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [providers, setProviders] = useState<any[]>([]);

  // sort providers derived from filtered providers so that sort can respect filters
  const [sortMethod, setSortMethod] = useState<string>("Ordenar por");

  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const providersPerPage = 10; // Number of providers per page

  const from = totalRecords > 0 ? (pageNumber - 1) * providersPerPage + 1 : 0;
  const to =
    pageNumber < totalPages ? pageNumber * providersPerPage : totalRecords;

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth", // Smooth scrolling effect
    });
  };

  useEffect(() => {
    scrollToTop();
    setTempFilters(filters);
  }, [filters]);

  // Read filters from URL and set them in state
  useEffect(() => {
    const categoryFromParams = searchParams.get("category");
    const locationFromParams = searchParams.get("location");
    const ratingFromParams = searchParams.get("rating");
    const verifiedFromParams = searchParams.get("verified");
    // const subCategoryFromParams = searchParams.get("subCategory");

    const newFilters = { ...filters };

    if (categoryFromParams) {
      newFilters.category = categoryFromParams.split(",");
    } else {
      newFilters.category = [];
    }

    if (locationFromParams) {
      newFilters.location = locationFromParams.split(",");
    } else {
      newFilters.location = [];
    }

    // if (subCategoryFromParams) {
    //   newFilters.subCategory = subCategoryFromParams.split(",");
    // } else {
    //   newFilters.subCategory = [];
    // }

    if (ratingFromParams) {
      newFilters.rating = ratingFromParams;
    } else {
      newFilters.rating = null;
    }
    if (verifiedFromParams) {
      newFilters.verified = verifiedFromParams;
    } else {
      newFilters.verified = null;
    }

    setFilters(newFilters);
    setTempFilters(newFilters);
  }, [searchParams]);

  const userQuery = searchParams.get("userQuery")?.toLowerCase() ?? "";

  useEffect(() => {
    const abortController = new AbortController();

    const fetchProviders = async () => {
      const categories = getProviderCategoriesByName(tempFilters.category);
      const locations = getLocationsByName(tempFilters.location);
      let verified = [];
      let rating = [];

      if (tempFilters.verified) {
        verified.push(tempFilters.verified);
      }

      if (tempFilters.rating) {
        rating.push(tempFilters.rating);
      }

      const search = userQuery ? userQuery.trim() : "";
      const orderBy = getOrderObject(sortMethod);

        const providers = await getProviders({
          params: {
            pageSize: providersPerPage,
            tagIds: [...categories, ...locations, ...verified, ...rating],
            searchTerm: search,
            ...orderBy,
            pageNumber: pageNumber,
          },
        });

        if (abortController.signal.aborted) return;

        const pagination = providers.paginationInfo;
        setTotalPages(pagination.totalPages);
        setTotalRecords(pagination.totalRecords);

        const data = await Promise.all(
          providers.data.map(async provider => ({
            id: provider.id,
            uniqueName: provider.uniqueName,
            name: provider.name,
            verified: isVerifiedUser(provider.tagIds),
            Location: getLocations(provider.tagIds),
            Category: getProviderCategories(provider.tagIds),
            SubCategory: getSubCategories(provider.tagIds),
            image: await getImageByPosition(provider.logo, true),
            rating: getRating(provider.rating),
            rated: provider.ratingCount,
          }))
        );

        if (!abortController.signal.aborted) {
          setProviders(data);
        }
    };

    fetchProviders();

    // Cleanup: abort previous request when dependencies change
    return () => abortController.abort();
  }, [tempFilters, sortMethod, pageNumber]);

  const handleApplyFilters = () => {
    if (JSON.stringify(filters) !== JSON.stringify(tempFilters)) {
      setFilters(tempFilters);
      const params = new URLSearchParams();
      if (tempFilters.category.length > 0) {
        params.set("category", tempFilters.category.join(","));
      } else {
        params.delete("category");
      }
      if (tempFilters.location.length > 0) {
        params.set("location", tempFilters.location.join(","));
      } else {
        params.delete("location");
      }
      // if (tempFilters.subCategory.length > 0) {
      //   params.set("subCategory", tempFilters.subCategory.join(","));
      // } else {
      //   params.delete("subCategory");
      // }
      if (tempFilters.rating !== null) {
        params.set("rating", tempFilters.rating); // Convert array to string
      } else {
        params.delete("rating");
      }
      if (tempFilters.verified !== null) {
        params.set("verified", tempFilters.verified);
      } else {
        params.delete("verified");
      }
      setSearchParams(params);
    }
  };

  const countAppliedFilters = () => {
    let count = 0;
    count += filters.location.length;
    count += filters.category.length;
    // count += filters.subCategory.length;

    if (filters.rating !== null) count += 1;
    if (filters.verified !== null) count += 1;

    return count;
  };

  const appliedFiltersCount = countAppliedFilters();

  return (
    <div>
      <PhoenixOffcanvas
        open={show}
        onHide={handleClose}
        style={{ width: 300, top: 92 }}
        className="py-5 ps-5"
        fixed
      >
        <Scrollbar className="table-scrollbar">
          <div className="pe-5">
            <ProductFilterItems
              handleClose={handleClose}
              tempFilters={tempFilters}
              setTempFilters={setTempFilters}
              onApply={handleApplyFilters}
            />
          </div>
        </Scrollbar>
      </PhoenixOffcanvas>

      <Section className="pt-5 pb-9 ">
        <div className="d-flex justify-content-between container-md ">
          {/* On large screens: placeholder div, for when button is visible we line up with dropdown */}
          <div className="d-none d-lg-block"></div>
          {/* Component will let user select a sort method, and let parent know to sort by method chosen. */}
          {/* <DropdownSortProviders
            sortMethod={sortMethod}
            onSelectSort={(method: string) => setSortMethod(method)}
          /> */}
        </div>

        <Row>
          <Col lg={3} xxl={2} className="d-none d-lg-block ps-xl-0 ps-xxl-3">
            <div
              className="position-sticky"
              style={{ top: "1rem", height: "calc(100vh - 2rem) " }}
            >
              <Scrollbar className="product-scrollbar">
                <ProductFilterItems
                  handleClose={handleClose}
                  tempFilters={tempFilters}
                  setTempFilters={setTempFilters}
                  onApply={handleApplyFilters}
                />
              </Scrollbar>
            </div>
          </Col>

          <Col lg={9} xxl={10}>
            <div className="d-flex justify-content-between flex-wrap">
              {/* Filtros button */}
              <Button
                variant="phoenix-secondary"
                size="sm"
                className="text-700 mb-5 d-lg-none"
                onClick={handleShow}
              >
                <FontAwesomeIcon className="px-2" icon={faSliders} />
                Filtros {appliedFiltersCount > 0 && `(${appliedFiltersCount})`}
              </Button>

              {/* hide on smaller screen, show like this on large */}
              <div className="d-none d-lg-block">
                {from}-{to} <span style={{ color: "grey" }}>de</span>{" "}
                {totalRecords} resultados
                {userQuery && <> para "{userQuery}"</>}
              </div>

              {/* Dropdown */}
              <DropdownSortProviders
                sortMethod={sortMethod}
                onSelectSort={(method: string) => setSortMethod(method)}
              />

              {/* Text appears on its own row on small screens */}
              <div className="d-block d-lg-none w-100 mt-2">
                {from}-{to} <span style={{ color: "grey" }}>de</span>{" "}
                {totalRecords} resultados
                {userQuery && <> para "{userQuery}"</>}
              </div>
            </div>
            <Row>
              <div className="d-flex "></div>
            </Row>
            {providers.length > 0 ? (
              <>
                <Row className="gx-3 gy-6 mb-8 pt-5 custom-five-cols">
                  {providers.map((product: any) => (
                    <Col
                      xs={6}
                      sm={6}
                      md={4}
                      lg={3}
                      className="custom-col"
                      key={product.id}
                    >
                      <div className="h-100">
                        <ProductCard product={product} />
                      </div>
                    </Col>
                  ))}
                </Row>
                <Pagination className="mb-0 justify-content-end">
                  <Pagination.Prev
                    onClick={() =>
                      pageNumber > 1 && setPageNumber(pageNumber - 1)
                    }
                    disabled={pageNumber === 1}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </Pagination.Prev>

                  {[...Array(totalPages)].map((_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === pageNumber}
                      onClick={() => setPageNumber(index + 1)}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}

                  <Pagination.Next
                    onClick={() =>
                      pageNumber < totalPages && setPageNumber(pageNumber + 1)
                    }
                    disabled={pageNumber === totalPages}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Pagination.Next>
                </Pagination>
              </>
            ) : (
              <div className="text-center py-5">
                <h5>No se encontraron proveedores</h5>
              </div>
            )}
          </Col>
        </Row>
      </Section>
    </div>
  );
};

export default ProvidersFilter;
